import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Card, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { Bot, Check, Link2 } from 'lucide-react';
import classNames from 'classnames';
import mizfaMark from '@/assets/img/mizfa-tools-no-text.svg';
import { useAppContext } from '@/Main';
import {
  completeMcpOAuthConsent,
  toMcpApiErrorMessage
} from '../api/mcpOAuthApi';

/** Beige and yellow primaries are too light for a white check. */
const LIGHT_PRIMARY_VARIANTS = new Set(['primary', 'yellow']);

const exchangedCodes = new Set<string>();

type Phase = 'working' | 'done' | 'error';

function StepMark({ state }: { state: 'done' | 'active' | 'wait' }) {
  const {
    config: { themeVariant }
  } = useAppContext() as { config: { themeVariant?: string } };
  const lightPrimary = LIGHT_PRIMARY_VARIANTS.has(themeVariant || '');

  return (
    <span
      className={classNames(
        'd-inline-flex align-items-center justify-content-center rounded-circle border flex-shrink-0',
        state === 'done' && 'bg-primary border-primary',
        state === 'done' && (lightPrimary ? 'text-black' : 'text-white'),
        state === 'active' && 'border-primary bg-white',
        state === 'wait' && 'bg-body-tertiary'
      )}
      style={{ width: 28, height: 28 }}
    >
      {state === 'done' && <Check size={14} aria-hidden />}
      {state === 'active' && (
        <Spinner animation="border" size="sm" variant="primary" style={{ width: 14, height: 14 }} />
      )}
      {state === 'wait' && (
        <span className="rounded-circle bg-300" style={{ width: 8, height: 8 }} />
      )}
    </span>
  );
}

function StepRow({
  state,
  title,
  detail,
  last
}: {
  state: 'done' | 'active' | 'wait';
  title: string;
  detail: string;
  last?: boolean;
}) {
  return (
    <div className="d-flex gap-3 text-start">
      <div className="d-flex flex-column align-items-center">
        <StepMark state={state} />
        {!last && (
          <span
            className="my-1"
            style={{
              width: 2,
              minHeight: 28,
              background: state === 'done' ? 'var(--falcon-primary)' : 'var(--falcon-200)'
            }}
          />
        )}
      </div>
      <div className={classNames('pt-1', !last && 'pb-2')}>
        <div className={classNames('fw-semi-bold fs--1', state === 'wait' && 'text-600')}>{title}</div>
        <div className="text-600 fs--1">{detail}</div>
      </div>
    </div>
  );
}

function BrandPair() {
  return (
    <div
      className="d-flex align-items-center justify-content-center gap-2 mb-4"
      role="img"
      aria-label="اتصال میزفا تولز به Agent"
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle border bg-white shadow-sm flex-shrink-0"
        style={{ width: 64, height: 64 }}
      >
        <img src={mizfaMark} alt="" width={36} height={36} />
      </div>
      <span className="d-inline-flex align-items-center text-primary" aria-hidden>
        <span
          className="rounded-pill"
          style={{ width: 22, height: 2, background: 'var(--falcon-primary)' }}
        />
        <Link2 size={16} className="mx-1" />
        <span
          className="rounded-pill"
          style={{ width: 22, height: 2, background: 'var(--falcon-primary)' }}
        />
      </span>
      <div
        className="d-flex align-items-center justify-content-center rounded-circle border bg-white shadow-sm flex-shrink-0"
        style={{ width: 64, height: 64, color: '#333' }}
      >
        <Bot size={28} aria-hidden />
      </div>
    </div>
  );
}

function StatusCard({
  title,
  detail,
  children
}: {
  title: string;
  detail: string;
  children: ReactNode;
}) {
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center px-3 py-4">
      <Card
        style={{ maxWidth: 440 }}
      >
        <div className="border-top border-3 border-primary" />
        <Card.Body className="px-4 pt-4 pb-4">
          <div className="text-center mb-4">
            <BrandPair />
            <h5 className="mb-2">{title}</h5>
            <p className="fs--1 text-600 mb-0">{detail}</p>
          </div>
          {children}
        </Card.Body>
      </Card>
    </div>
  );
}

export default function ConnectCallbackPage() {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('working');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = (params.get('code') ?? '').trim();
    const state = (params.get('state') ?? '').trim();
    const oauthError = (params.get('error') ?? '').trim();
    if (oauthError) {
      setPhase('error');
      setError('ورود انجام نشد. اتصال را دوباره از همان Agent شروع کنید.');
      return;
    }
    if (!code || !state) {
      setPhase('error');
      setError('اتصال ناموفق بود. دوباره از همان برنامه شروع کنید.');
      return;
    }
    const exchangeKey = `${state}:${code}`;
    if (exchangedCodes.has(exchangeKey)) return;
    exchangedCodes.add(exchangeKey);

    void (async () => {
      try {
        const done = await completeMcpOAuthConsent(state, code);
        setPhase('done');
        window.location.assign(done.redirectTo);
      } catch (caught: unknown) {
        exchangedCodes.delete(exchangeKey);
        setPhase('error');
        setError(
          toMcpApiErrorMessage(
            caught,
            caught instanceof Error ? caught.message : 'اتصال به Agent ناموفق بود'
          )
        );
      }
    })();
  }, [params]);

  if (phase === 'error') {
    const detail = 'Agent هنوز وصل نشده است. از همان برنامه دوباره Connect را بزنید.';
    const extra = error && error !== detail ? error : null;
    return (
      <StatusCard title="اتصال کامل نشد" detail={detail}>
        {extra && (
          <Alert variant="danger" className="fs--1 text-center mb-0">
            {extra}
          </Alert>
        )}
      </StatusCard>
    );
  }

  const finished = phase === 'done';

  return (
    <StatusCard
      title={finished ? 'اتصال کامل شد' : 'در حال وصل کردن'}
      detail={
        finished
          ? 'اگر پنجرهٔ Agent خودش باز نشد، به همان برنامه برگردید. اتصال آنجاست.'
          : 'ورود شما ثبت شد. اتصال را می‌سازیم و شما را به Agent برمی‌گردانیم.'
      }
    >
      <div className="rounded-3 border bg-body-tertiary px-3 py-3">
        <StepRow state="done" title="ورود" detail="حساب شما در میزفا تولز تأیید شد" />
        <StepRow
          state={finished ? 'done' : 'active'}
          title="ساخت اتصال"
          detail={finished ? 'Agent به حساب میزفا تولز وصل شد' : 'در حال وصل کردن همین Agent'}
        />
        <StepRow
          state={finished ? 'done' : 'wait'}
          title="بازگشت به Agent"
          detail={finished ? 'چند لحظه دیگر همان پنجره باز می‌شود' : 'بعد از ساخت اتصال برمی‌گردید'}
          last
        />
      </div>
    </StatusCard>
  );
}
